import { CalendarChallenge } from 'src/types/shared/Challenge';
import { getDateRange, getMonthsFromDates } from '../../../utils/generic';
import { Challenge, challengeStageTypes, FormattedDate } from '../../../types/shared/Challenge';

const useChallengeCalendar = (
    challenges: CalendarChallenge[],
    startDate: Date,
    endDate: Date,
    selectedChallengeID: number | null,
) => {
    const monthHeaderHeight = 25;
    const dayHeaderHeight = 25;
    const rowHeight = 30;
    const dayWidth = 20;

    const dateRange = getDateRange(startDate, endDate);

    const formattedDateRange = dateRange.map(date => {
        const dA = date.split('-');
        return {
            y: dA[0],
            m: dA[1],
            d: dA[2],
        };
    });

    const selectedChallenge =
        challenges.find(challenge => challenge.id === selectedChallengeID) || ({} as Challenge);

    const monthHeaders = getMonthsFromDates(formattedDateRange);
    const dayHeaders = formattedDateRange.map(({ y, m, d }: FormattedDate) => `${y}-${m}-${d}`);

    const buildGridRows = (challenges: any[]): string => {
        let result = `[rows-start] ${monthHeaderHeight}px [header-month-end header-day-start] ${dayHeaderHeight}px [header-day-end`; // Header rows
        if (!challenges.length) result += ']';
        else {
            result += ' ';
            challenges.forEach((challenge, i) => {
                const name = `challenge-${challenge.id}`;

                result += `${name}-start] ${rowHeight}px [${name}-end`;

                if (!!challenges[i + 1] || selectedChallengeID === challenge.id) result += ' ';
                else result += ']';

                // If challenge is selected, draw extra rows
                if (selectedChallengeID === challenge.id) {
                    challengeStageTypes.forEach(type => {
                        result += `${name}-${type}-start] ${rowHeight}px [${name}-${type}-end`;
                        if (type !== challengeStageTypes[challengeStageTypes.length - 1])
                            result += ' ';
                    });
                    if (!!challenges[i + 1]) result += ' ';
                    else result += ' rows-spacer-start]';
                }
            });
            result += ' 1fr [rows-end]'; // Extra spacing row at the end for scroll bars
        }
        return result;
    };

    const buildGridCols = (dates: FormattedDate[]): string => {
        let result = '[cols-start] 20rem [sidebar-end] ';
        dates.forEach(({ y, m, d }, i) => {
            const name = `${y}-${m}-${d}`;
            if (i > 0) result += `start-${name}] `;
            result += `${dayWidth}px [`;
            if (!!dates[i + 1]) result += `end-${name} `;
        });
        result += 'cols-end]';
        return result;
    };

    const buildGridAreas = (challenges: any[], dates: FormattedDate[]): string => {
        const result: string[] = [];
        // Month Headers
        let headerMonthRow = `"sidebar-month `;
        dates.forEach((date, i) => {
            const { y, m } = date;
            headerMonthRow += `header-month-${y}-${m}${i < dates.length - 1 ? ' ' : ''}`;
        });
        headerMonthRow += `"`;
        result.push(headerMonthRow);

        // Day Headers
        let headerDayRow = `"sidebar-day `;
        dates.forEach((date, i) => {
            const { y, m, d } = date;
            headerDayRow += `header-day-${y}-${m}-${d}${i < dates.length - 1 ? ' ' : ''}`;
        });
        headerDayRow += `"`;
        result.push(headerDayRow);

        // Challenges
        challenges.forEach((challenge, i) => {
            let subResult = `"sidebar-challenge-${challenge.id}`;
            dates.forEach(({ y, m, d }, i) => {
                subResult += `challenge-${challenge.id}-${y}-${m}-${d}${
                    i < dates.length - 1 ? ' ' : ''
                }`;
            });
            subResult += `"`;
            result.push(subResult);

            // Selected challenge - extra rows
            if (selectedChallengeID === challenge.id) {
                challengeStageTypes.forEach(type => {
                    let subResult = `"sidebar-challenge-${challenge.id}-${type}`;
                    dates.forEach(({ y, m, d }, i) => {
                        subResult += `challenge-${challenge.id}-${type}-${y}-${m}-${d}${
                            i < dates.length - 1 ? ' ' : ''
                        }`;
                    });
                    subResult += `"`;
                    result.push(subResult);
                });
            }
        });

        let subResult = `"sidebar-end `;
        dates.forEach(({ y, m, d }, i) => {
            subResult += `end-${y}-${m}-${d}${i < dates.length - 1 ? ' ' : ''}`;
        });
        subResult += `"`;
        result.push(subResult);

        return result.join(' ');
    };

    return {
        monthHeaderHeight,
        dayHeaderHeight,
        rowHeight,
        dayWidth,
        monthHeaders,
        dayHeaders,
        formattedDateRange,
        selectedChallenge,
        buildGridRows,
        buildGridCols,
        buildGridAreas,
    };
};

export default useChallengeCalendar;
